// v2/pages/admin/AdminCustomersV2.js — Yonetim / Musteriler (/v2/admin/customers)
// Filtreler adres cubugunda tutulur: ?q= &scope= &group= &users= &restricted= &debtor=
//                                    &pending= &sort= &dir= &page= &per=
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Store, Search, X, SlidersHorizontal, RotateCw, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown, Users, Wallet, ShoppingCart, ShieldAlert,
  Inbox, AlertTriangle, UserX,
} from 'lucide-react';
import useAdminCustomers from '../../hooks/useAdminCustomers';
import { formatTRY, formatTRYShort, formatNumber } from '../../utils/format';
import { SkelPanel } from '../../components/Skeleton';
import s from '../../styles/AdminCustomersV2.module.css';
import '../../styles/tokens.css';

const KAPSAMLAR = [
  { id: 'b2b', label: 'B2B müşterileri', ipucu: 'Kullanıcısı veya siparişi olan cariler' },
  { id: 'active', label: 'Hareketli cariler', ipucu: 'Cari hesap hareketi olan kayıtlar' },
  { id: 'all', label: 'Tüm cariler', ipucu: 'Muhasebe carileri dahil tüm kayıtlar' },
];
const KULLANICI = [
  { id: '', label: 'Kullanıcı: hepsi' },
  { id: 'with', label: 'Kullanıcısı olanlar' },
  { id: 'without', label: 'Kullanıcısı olmayanlar' },
];
const ANAHTARLAR = [
  { id: 'restricted', label: 'Kısıtlı', ikon: ShieldAlert },
  { id: 'debtor', label: 'Borçlu', ikon: Wallet },
  { id: 'pending', label: 'Sepette bekleyen', ikon: ShoppingCart },
];
const KOLONLAR = [
  { id: 'code', label: 'Kod', sort: 'code' },
  { id: 'title', label: 'Ünvan', sort: 'title' },
  { id: 'group', label: 'Grup' },
  { id: 'users', label: 'Kullanıcı', sort: 'users', num: true },
  { id: 'orders', label: 'Sipariş', sort: 'orders', num: true },
  { id: 'last_order', label: 'Son Sipariş', sort: 'last_order' },
  { id: 'balance', label: 'Bakiye', sort: 'balance', num: true },
  { id: 'revenue', label: 'Ciro', sort: 'revenue', num: true },
];
const VARSAYILAN = { scope: 'all', sort: 'revenue', dir: 'desc', page: '1', per: '20' };

const tarihTR = (ymd) => {
  if (!ymd) return '-';
  const [y, a, g] = String(ymd).split('-');
  return y ? `${g}.${a}.${y}` : '-';
};

export default function AdminCustomersV2() {
  const [sp, setSp] = useSearchParams();
  const oku = useCallback((k) => sp.get(k) || VARSAYILAN[k] || '', [sp]);

  const [arama, setArama] = useState(sp.get('q') || '');
  const [filtreAcik, setFiltreAcik] = useState(false);

  // adres cubugu disaridan degisirse (geri tusu) kutuyu esitle
  useEffect(() => { setArama(sp.get('q') || ''); }, [sp]);

  const params = useMemo(() => ({
    q: sp.get('q') || '',
    scope: oku('scope'),
    group: sp.get('group') || '',
    users: sp.get('users') || '',
    restricted: sp.get('restricted') || '',
    debtor: sp.get('debtor') || '',
    pending: sp.get('pending') || '',
    sort: oku('sort'),
    dir: oku('dir'),
    page: oku('page'),
    per: oku('per'),
  }), [sp, oku]);

  const { data, loading, error, yenile } = useAdminCustomers(params);

  const guncelle = useCallback((yeni, sayfaSifirla = true) => {
    setSp((onceki) => {
      const p = new URLSearchParams(onceki);
      Object.entries(yeni).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined || v === VARSAYILAN[k]) p.delete(k);
        else p.set(k, v);
      });
      if (sayfaSifirla) p.delete('page');
      return p;
    }, { replace: true });
  }, [setSp]);

  // arama kutusu: yazmayi birakinca 400 ms sonra uygula
  useEffect(() => {
    const suAn = sp.get('q') || '';
    if (arama === suAn) return undefined;
    const t = setTimeout(() => guncelle({ q: arama.trim() }), 400);
    return () => clearTimeout(t);
  }, [arama, sp, guncelle]);

  const sirala = (alan) => {
    if (!alan) return;
    const ayni = params.sort === alan;
    guncelle({ sort: alan, dir: ayni && params.dir === 'desc' ? 'asc' : 'desc' });
  };

  const ilk = loading && !data;
  const ogeler = (data && data.items) || [];
  const ozet = (data && data.summary) || {};
  const gruplar = (data && data.groups) || [];
  const sayfa = (data && data.page) || 1;
  const sonSayfa = (data && data.total_pages) || 1;
  const toplam = (data && data.total) || 0;
  const per = Number(params.per) || 20;
  const ilkSira = (sayfa - 1) * per;

  const acikFiltre = ['group', 'users', 'restricted', 'debtor', 'pending']
    .filter((k) => params[k]).length + (params.scope !== 'all' ? 1 : 0);

  const SiraIkon = ({ alan }) => {
    if (!alan) return null;
    if (params.sort !== alan) return <ChevronsUpDown size={13} className={s.sortIdle} />;
    return params.dir === 'asc'
      ? <ChevronUp size={13} className={s.sortOn} />
      : <ChevronDown size={13} className={s.sortOn} />;
  };

  return (
    <div className={s.page}>
      <div className={s.head}>
        <div className={s.headText}>
          <h2 className={s.title}>Müşteriler</h2>
          <p className={s.subtitle}>
            {ilk ? 'Yükleniyor…' : <><b>{formatNumber(toplam)}</b> kayıt · tutarlar KDV hariç</>}
          </p>
        </div>
        <button type="button" className={s.ghostBtn} onClick={yenile} disabled={loading}>
          <RotateCw size={15} className={loading ? s.spin : ''} />
          <span className={s.btnText}>Yenile</span>
        </button>
      </div>

      <div className={s.stats}>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tBlue}`}><Store size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(ozet.customers)}</span>
            <span className={s.statLabel}>Müşteri</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tGreen}`}><Users size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(ozet.with_users)}</span>
            <span className={s.statLabel}>Kullanıcısı olan</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tRed}`}><Wallet size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatTRYShort(ozet.debt_total)}</span>
            <span className={s.statLabel}>Toplam alacak</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tAmber}`}><ShoppingCart size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(ozet.with_pending)}</span>
            <span className={s.statLabel}>Sepette bekleyen</span>
          </span>
        </div>
      </div>

      <div className={s.toolbar}>
        <div className={s.searchBox}>
          <Search size={16} className={s.searchIcon} />
          <input
            type="search"
            className={s.search}
            placeholder="Ünvan, kod veya e-posta ile ara…"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            aria-label="Müşteri ara"
          />
          {arama && (
            <button type="button" className={s.clearBtn} onClick={() => setArama('')} aria-label="Aramayı temizle">
              <X size={15} />
            </button>
          )}
        </div>
        <button
          type="button"
          className={`${s.ghostBtn} ${filtreAcik ? s.ghostOn : ''}`}
          onClick={() => setFiltreAcik((v) => !v)}
          aria-expanded={filtreAcik}
        >
          <SlidersHorizontal size={15} />
          <span className={s.btnText}>Filtreler</span>
          {acikFiltre > 0 && <span className={s.badge}>{acikFiltre}</span>}
        </button>
      </div>

      {filtreAcik && (
        <div className={s.filters}>
          <label className={s.field}>
            <span className={s.fieldLabel}>Kapsam</span>
            <select
              className={s.select} value={params.scope}
              onChange={(e) => guncelle({ scope: e.target.value })}
            >
              {KAPSAMLAR.map((k) => <option key={k.id} value={k.id} title={k.ipucu}>{k.label}</option>)}
            </select>
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Grup</span>
            <select
              className={s.select} value={params.group}
              onChange={(e) => guncelle({ group: e.target.value })}
            >
              <option value="">Tüm gruplar</option>
              {gruplar.map((g) => (
                <option key={g.code} value={g.code}>{g.label} ({formatNumber(g.count)})</option>
              ))}
            </select>
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Kullanıcı</span>
            <select
              className={s.select} value={params.users}
              onChange={(e) => guncelle({ users: e.target.value })}
            >
              {KULLANICI.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </label>
          <div className={s.toggles}>
            {ANAHTARLAR.map((a) => {
              const Ikon = a.ikon;
              const acik = params[a.id] === '1';
              return (
                <button
                  key={a.id} type="button"
                  className={`${s.toggle} ${acik ? s.toggleOn : ''}`}
                  onClick={() => guncelle({ [a.id]: acik ? '' : '1' })}
                  aria-pressed={acik}
                >
                  <Ikon size={14} /> {a.label}
                </button>
              );
            })}
            {(acikFiltre > 0 || params.q) && (
              <button type="button" className={s.resetBtn} onClick={() => setSp({}, { replace: true })}>
                <X size={14} /> Filtreleri temizle
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className={s.alert}>
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button type="button" className={s.alertBtn} onClick={yenile}>Tekrar dene</button>
        </div>
      )}

      <section className={s.panel}>
        {ilk ? (
          <div className={s.panelBody}><SkelPanel rows={6} head={false} /></div>
        ) : ogeler.length === 0 ? (
          <div className={s.empty}>
            <Inbox size={24} />
            <p className={s.emptyTitle}>Kayıt bulunamadı</p>
            <p className={s.emptyText}>Arama veya filtreleri değiştirmeyi deneyin.</p>
          </div>
        ) : (
          <>
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={`${s.th} ${s.thNo}`}>#</th>
                    {KOLONLAR.map((k) => (
                      <th
                        key={k.id}
                        className={`${s.th} ${k.num ? s.num : ''} ${k.sort ? s.thSort : ''}`}
                        onClick={() => sirala(k.sort)}
                        title={k.sort ? 'Sıralamak için tıklayın' : ''}
                      >
                        <span className={s.thInner}>{k.label}<SiraIkon alan={k.sort} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ogeler.map((c, i) => (
                    <tr key={c.code}>
                      <td className={`${s.td} ${s.tdNo}`}>{ilkSira + i + 1}</td>
                      <td className={`${s.td} ${s.code}`}>
                        <Link to={`/v2/admin/customers/${encodeURIComponent(c.code)}`} className={s.codeLink}>{c.code}</Link>
                      </td>
                      <td className={s.td}>
                        <span className={s.titleCell}>
                          <Link to={`/v2/admin/customers/${encodeURIComponent(c.code)}`} className={s.custLink} title={c.title}>{c.title}</Link>
                          <span className={s.chips}>
                            {c.restricted && <span className={`${s.chip} ${s.chipRed}`}>Kısıtlı</span>}
                            {c.pending > 0 && <span className={`${s.chip} ${s.chipAmber}`}>{formatNumber(c.pending)} bekleyen</span>}
                            {c.active_users === 0 && <span className={`${s.chip} ${s.chipGray}`}><UserX size={11} /> kullanıcısız</span>}
                            {c.email && <span className={s.email} title={c.email}>{c.email}</span>}
                          </span>
                        </span>
                      </td>
                      <td className={s.td}><span className={s.group}>{c.group || '-'}</span></td>
                      <td className={`${s.td} ${s.num}`}>
                        {formatNumber(c.active_users)}
                        {c.total_users !== c.active_users && <span className={s.dim}>/{formatNumber(c.total_users)}</span>}
                      </td>
                      <td className={`${s.td} ${s.num}`}>{formatNumber(c.orders)}</td>
                      <td className={`${s.td} ${s.muted}`}>{tarihTR(c.last_order)}</td>
                      <td className={`${s.td} ${s.num} ${c.balance > 0 ? s.debt : s.credit}`}>{formatTRY(c.balance)}</td>
                      <td className={`${s.td} ${s.num} ${s.strong}`}>{formatTRYShort(c.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className={s.mList}>
              {ogeler.map((c) => (
                <li key={`m-${c.code}`} className={s.mItem}>
                  <div className={s.mTop}>
                    <Link to={`/v2/admin/customers/${encodeURIComponent(c.code)}`} className={s.titleCell}>
                      <span className={s.custLink}>{c.title}</span>
                      <span className={s.custCode}>{c.code}{c.group ? ` · Grup ${c.group}` : ''}</span>
                    </Link>
                    <span className={`${s.mBalance} ${c.balance > 0 ? s.debt : s.credit}`}>{formatTRY(c.balance)}</span>
                  </div>
                  <div className={s.mMeta}>
                    <span>{formatNumber(c.active_users)} kullanıcı</span>
                    <span>{formatNumber(c.orders)} sipariş</span>
                    <span>Son: {tarihTR(c.last_order)}</span>
                    <span>Ciro: {formatTRYShort(c.revenue)}</span>
                  </div>
                  {(c.restricted || c.pending > 0 || c.active_users === 0) && (
                    <div className={s.chips}>
                      {c.restricted && <span className={`${s.chip} ${s.chipRed}`}>Kısıtlı</span>}
                      {c.pending > 0 && <span className={`${s.chip} ${s.chipAmber}`}>{formatNumber(c.pending)} bekleyen</span>}
                      {c.active_users === 0 && <span className={`${s.chip} ${s.chipGray}`}>kullanıcısız</span>}
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className={s.pager}>
              <span className={s.pagerInfo}>
                {formatNumber(ilkSira + 1)}–{formatNumber(ilkSira + ogeler.length)} / {formatNumber(toplam)}
              </span>
              <label className={s.perBox}>
                <span className={s.fieldLabel}>Sayfa başına</span>
                <select
                  className={s.selectSm} value={params.per}
                  onChange={(e) => guncelle({ per: e.target.value })}
                >
                  {((data && data.page_sizes) || [20, 50, 100]).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <div className={s.pagerBtns}>
                <button
                  type="button" className={s.pageBtn} disabled={sayfa <= 1 || loading}
                  onClick={() => guncelle({ page: String(sayfa - 1) }, false)}
                  aria-label="Önceki sayfa"
                ><ChevronLeft size={16} /></button>
                <span className={s.pageNo}>{formatNumber(sayfa)} / {formatNumber(sonSayfa)}</span>
                <button
                  type="button" className={s.pageBtn} disabled={sayfa >= sonSayfa || loading}
                  onClick={() => guncelle({ page: String(sayfa + 1) }, false)}
                  aria-label="Sonraki sayfa"
                ><ChevronRight size={16} /></button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
